import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@fragments';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { drawPage } from '../../../render/renderer';
import { onResourcesChange } from '../../../render/resources';
import { ensureDocumentFonts, onFontsLoaded } from '../../../model/fonts';
import { invalidateTextLayouts } from '../../../model/textLayout';
import { hitTest, layerBounds, snapBounds } from '../../../model/geometry';
import { boxDelta, framingOf, framingPatch, panFocus, zoomFocus } from '../../../model/framing';
import { patchLayer, photoFrameOf } from '../../../model/operations';
import { pageAt } from '../../../model/timeline';
import SelectionBox, { placeBox } from './components/SelectionBox';
import TextEditor from './components/TextEditor';
import { gesturePatch, moveLayer, resizeLayer, rotateLayer } from './transform';
import { gsap, reducedMotion } from '../../motion';
import './styles.scss';

import { fitScale, pagerBeside, pinchZoom } from './zoom';

export { fitScale, nextZoom, pinchZoom } from './zoom';

// The artboard: the canvas painted by the shared renderer, and an overlay for picking,
// hovering, the selection frame, snapping guides and in-place text editing. Gestures paint
// a draft every animation frame without touching React; releasing commits one step. A
// carousel gets arrows beside the art to step through its pages.
const MAX_PIXELS = 16e6;

// 2.13.0: with `playback`, the page shows as at its playhead (the clips on then, each its
// player's frame), painted again on every tick while it plays; a click reaches only what shows.
// A video file dropped on the art goes to `onDropVideo`.
export default function Stage({ editor, readOnly, highlight, onScale, onDropFiles, onDropVideo, onLayerMenu, playback = null }) {
  const { t } = useTranslation('artEditor');
  const { document: doc, selected, editing } = editor;
  const [clock, setClock] = useState(() => playback?.getState().time || 0);
  // What the pointer reaches: the page as at the playhead (paused only; a playing page is
  // picked as at the frame it stopped on, the same frames shown).
  const page = pageAt(editor.page, clock);
  const viewportRef = useRef(null);
  const boardRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const hoverRef = useRef(null);
  const guidesRef = useRef(null);
  const selectionRef = useRef(null);
  const highlightRef = useRef(null);
  const draft = useRef(null);
  const gesture = useRef(null);
  const frame = useRef(0);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion(value => value + 1), []);

  useLayoutEffect(() => {
    const element = viewportRef.current;
    const observer = new ResizeObserver(([entry]) => setViewport({ width: entry.contentRect.width, height: entry.contentRect.height }));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => onResourcesChange(bump), [bump]);
  useEffect(() => onFontsLoaded(() => { invalidateTextLayouts(); bump(); }), [bump]);
  useEffect(() => { if (doc) ensureDocumentFonts(doc); }, [doc]);

  const fit = fitScale(viewport, doc);
  const scale = editor.zoom === 'fit' ? fit : editor.zoom;
  useEffect(() => { onScale?.(scale, fit); }, [scale, fit]); // eslint-disable-line react-hooks/exhaustive-deps

  const paint = useRef(() => {});
  paint.current = () => {
    const canvas = canvasRef.current;
    if (!canvas || !doc || !page || !scale) return;
    const dpr = window.devicePixelRatio || 1;
    const pixels = Math.min(scale * dpr, Math.sqrt(MAX_PIXELS / (doc.width * doc.height)));
    const width = Math.round(doc.width * pixels);
    const height = Math.round(doc.height * pixels);
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    const ctx = canvas.getContext('2d');
    const time = playback ? playback.getState().time : 0;
    if (ctx) drawPage(ctx, doc, editor.page, { scale: pixels, skip: editing ? new Set([editing]) : null, override: draft.current, reveal: editor.cropping, time, frames: playback?.frame });
  };
  useLayoutEffect(() => { paint.current(); }, [doc, editor.page, scale, editing, editor.cropping, version]);
  const schedule = () => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => { frame.current = 0; paint.current(); });
  };
  useEffect(() => () => cancelAnimationFrame(frame.current), []);
  // Every tick paints the frame; the time a click picks by follows once playing stops.
  useEffect(() => playback?.subscribe(() => {
    schedule();
    const { playing, time } = playback.getState();
    if (!playing) setClock(time);
  }), [playback]); // eslint-disable-line react-hooks/exhaustive-deps

  // Another page of a carousel slides in from the side it comes from.
  const shownIndex = useRef(editor.pageIndex);
  useLayoutEffect(() => {
    const previous = shownIndex.current;
    shownIndex.current = editor.pageIndex;
    if (previous === editor.pageIndex || !boardRef.current || reducedMotion()) return undefined;
    const animation = gsap.fromTo(boardRef.current, { autoAlpha: 0.25, x: (editor.pageIndex > previous ? 1 : -1) * 32 },
      { autoAlpha: 1, x: 0, duration: 0.26, ease: 'power3.out', clearProps: 'transform,opacity,visibility' });
    return () => animation.kill();
  }, [page?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Layers the AI just changed glow once, so the creator sees what moved. A held highlight
  // (`persist`: the places an open question is about) fades in on each page and stays.
  const heldPage = highlight?.persist ? page?.id : null;
  useLayoutEffect(() => {
    const element = highlightRef.current;
    if (!element || !highlight?.ids?.length || reducedMotion()) return undefined;
    const boxes = element.querySelectorAll('.art-stage__changed');
    if (!boxes.length) return undefined;
    const animation = highlight.persist
      ? gsap.fromTo(boxes, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'sine.out', stagger: 0.05, clearProps: 'opacity,visibility' })
      : gsap.fromTo(boxes, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, yoyo: true, repeat: 3, ease: 'sine.inOut', stagger: 0.05 });
    return () => animation.kill();
  }, [highlight?.key, heldPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const toDoc = event => {
    const rect = boardRef.current.getBoundingClientRect();
    return { x: (event.clientX - rect.left) / scale, y: (event.clientY - rect.top) / scale };
  };
  const showGuides = guides => {
    const element = guidesRef.current;
    if (!element) return;
    element.replaceChildren(...guides.map(guide => {
      const line = document.createElement('span');
      line.className = `art-stage__guide is-${guide.axis}`;
      line.style[guide.axis === 'x' ? 'left' : 'top'] = `${guide.at * scale}px`;
      return line;
    }));
  };
  const setHover = layer => {
    const element = hoverRef.current;
    if (!element) return;
    if (!layer || selected.some(item => item.id === layer.id) || layer.id === editing) { element.style.display = 'none'; return; }
    element.style.display = 'block';
    placeBox(element, layer, scale);
  };

  const begin = (event, next) => {
    gesture.current = { ...next, pointerId: event.pointerId, moved: false };
    overlayRef.current.setPointerCapture?.(event.pointerId);
  };
  // Moving a photo inside its mask: a drag anywhere on the art moves it (the layer stays put);
  // a click away from the layer ends the mode.
  const cropping = editor.cropping ? page?.layers.find(layer => layer.id === editor.cropping) || null : null;
  const onPointerDown = event => {
    if (event.button !== 0 || !page) return;
    event.stopPropagation();
    viewportRef.current.closest('.art-editor')?.focus({ preventScroll: true });
    const point = toDoc(event);
    if (cropping && !readOnly) { begin(event, { type: 'crop', origin: cropping, start: point }); return; }
    const hit = hitTest(page, point, 4 / scale);
    if (!hit) { editor.select([]); return; }
    if (hit.id !== editing) editor.select([hit.id]);
    if (readOnly || hit.locked || hit.id === editing) return;
    const others = page.layers.filter(layer => layer.visible && layer.id !== hit.id).map(layerBounds);
    begin(event, { type: 'move', origin: hit, start: point, others });
  };
  const onHandleDown = (handle, event) => {
    if (event.button !== 0 || !selected[0]) return;
    event.stopPropagation();
    begin(event, { type: handle === 'rotate' ? 'rotate' : 'resize', handle, origin: selected[0], start: toDoc(event) });
  };
  const onPointerMove = event => {
    const current = gesture.current;
    if (!current) { if (event.pointerType === 'mouse' && page) setHover(hitTest(page, toDoc(event), 4 / scale)); return; }
    const point = toDoc(event);
    if (!current.moved && Math.hypot(point.x - current.start.x, point.y - current.start.y) * scale < 3) return;
    current.moved = true;
    let next;
    if (current.type === 'crop') {
      const { mx, my } = boxDelta(current.origin, point.x - current.start.x, point.y - current.start.y);
      current.focus = panFocus(current.origin, mx, my);
      draft.current = current.focus ? { [current.origin.id]: patchLayer(current.origin, framingPatch(current.origin, current.focus)) } : null;
      schedule();
      return;
    }
    if (current.type === 'move') {
      next = moveLayer(current.origin, current.start, point);
      if (!event.altKey) {
        const snap = snapBounds(layerBounds(next), doc, current.others, 6 / scale);
        next = { ...next, x: Math.round(next.x + snap.dx), y: Math.round(next.y + snap.dy) };
        showGuides(snap.guides);
      } else showGuides([]);
    } else if (current.type === 'resize') next = resizeLayer(current.origin, current.handle, point, { free: event.shiftKey });
    else next = rotateLayer(current.origin, point, { step: event.shiftKey });
    current.next = next;
    draft.current = { [next.id]: next };
    selectionRef.current?.place(next);
    setHover(null);
    schedule();
  };
  const onPointerUp = event => {
    const current = gesture.current;
    gesture.current = null;
    overlayRef.current?.releasePointerCapture?.(event.pointerId);
    showGuides([]);
    if (!current) return;
    draft.current = null;
    if (current.type === 'crop') {
      if (current.moved && current.focus) editor.update(current.origin.id, framingPatch(current.origin, current.focus), { key: null, label: 'crop' });
      else {
        // A click (no drag) away from the layer ends the mode and selects what is there (a
        // click on the photo's shade is on the photo).
        const hit = hitTest(page, toDoc(event), 4 / scale);
        if (photoFrameOf(page, hit)?.id !== current.origin.id) { editor.crop(null); editor.select(hit ? [hit.id] : []); }
        schedule();
      }
      return;
    }
    if (current.moved && current.next) {
      const patch = gesturePatch(current.origin, current.next);
      if (Object.keys(patch).length) editor.update(current.origin.id, patch, { key: null, label: current.type });
      else schedule();
    }
  };
  const onDoubleClick = event => {
    if (!page || readOnly) return;
    const hit = hitTest(page, toDoc(event), 4 / scale);
    if (hit?.type === 'text' && !hit.locked) editor.edit(hit.id);
    // A photo (an image, a photo-filled shape, under its shade too) is moved inside its frame
    // after a double-click.
    const photo = photoFrameOf(page, hit);
    if ((photo?.type === 'image' || photo?.type === 'video' || (photo?.type === 'shape' && photo.fill?.type === 'image')) && !photo.locked) editor.crop(photo.id);
  };
  // Zooming keeps the point under the pointer in place. Several events can land before React
  // renders the last one, so they chain off the pending target, not the rendered scale.
  const pinch = useRef(null);
  const onWheel = event => {
    // While moving a photo, the wheel zooms the photo inside its mask.
    if (cropping && !readOnly && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      const framing = framingOf(cropping);
      const next = framing && zoomFocus(cropping, pinchZoom(framing.zoom, event.deltaY, event.deltaMode) * 1);
      if (next) editor.update(cropping.id, framingPatch(cropping, next), { key: `${cropping.id}:crop-zoom` });
      return;
    }
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    if (!boardRef.current) return;
    const from = pinch.current?.target || scale;
    const target = pinchZoom(from, event.deltaY, event.deltaMode);
    if (Math.abs(target - from) < 1e-4) return;
    const rect = boardRef.current.getBoundingClientRect();
    const anchor = pinch.current?.anchor || { x: (event.clientX - rect.left) / scale, y: (event.clientY - rect.top) / scale };
    pinch.current = { target, anchor, clientX: event.clientX, clientY: event.clientY };
    editor.setZoom(target);
  };
  useLayoutEffect(() => {
    const current = pinch.current;
    pinch.current = null;
    if (!current || !boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    viewportRef.current.scrollLeft += rect.left + current.anchor.x * scale - current.clientX;
    viewportRef.current.scrollTop += rect.top + current.anchor.y * scale - current.clientY;
  }, [scale]);
  const onContextMenu = event => {
    if (!page || !onLayerMenu) return;
    // Locked layers click through, but a right-click can still reach one (to unlock it).
    const hit = hitTest(page, toDoc(event), 4 / scale, { locked: true });
    if (hit) onLayerMenu(hit, event);
  };
  useEffect(() => {
    // React's wheel listener is passive: pinch-zoom needs a real one to prevent the page zoom.
    const element = viewportRef.current;
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  });

  // A photo dropped on a shape fills it (the shape is outlined while dragged over; on a
  // photo's shade, the frame under it); dropped anywhere else it becomes a new image where it
  // lands.
  const frameAt = event => {
    if (!page || !boardRef.current) return null;
    const hit = photoFrameOf(page, hitTest(page, toDoc(event), 4 / scale));
    return hit?.type === 'shape' && hit.shape !== 'line' && !hit.locked ? hit : null;
  };
  const onDragOver = event => {
    if (readOnly || ![...event.dataTransfer.types].includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setHover(frameAt(event));
  };
  const onDrop = event => {
    setHover(null);
    const dropped = [...event.dataTransfer.files];
    const files = dropped.filter(file => file.type.startsWith('image/'));
    const video = onDropVideo ? dropped.find(file => file.type.startsWith('video/')) : null;
    if (readOnly || (!files.length && !video) || !boardRef.current) return;
    event.preventDefault();
    if (video) { onDropVideo(video); return; }
    onDropFiles?.(files, toDoc(event), frameAt(event));
  };

  const editingLayer = editing && page?.layers.find(layer => layer.id === editing);
  const single = selected.length === 1 && !editingLayer ? selected[0] : null;
  const changed = highlight?.ids?.length && page ? page.layers.filter(layer => highlight.ids.includes(layer.id) && layer.visible) : [];
  // A carousel steps through its pages with arrows beside the art (over its edges on a
  // narrow stage), read-only too. They sit on the stage, outside what scrolls, where the
  // fitted art's sides are: neither a page sliding in, a zoom nor a scroll moves them.
  const pager = doc && fit > 0 && doc.pages.length > 1
    ? { at: editor.pageIndex, count: doc.pages.length, place: pagerBeside(viewport, doc) ? '' : ' is-over', half: (doc.width * fit) / 2 } : null;
  return <div className="art-stage__shell" style={pager ? { '--pager-half': `${pager.half}px` } : undefined}>
    <div ref={viewportRef} className="art-stage" onPointerDown={event => { if (event.button === 0 && !gesture.current) editor.select([]); }} onDragOver={onDragOver} onDragLeave={() => setHover(null)} onDrop={onDrop}>
    {doc && scale > 0 && <div className={`art-stage__frame${pager && !pager.place ? ' has-pager' : ''}`}>
    <div ref={boardRef} className="art-stage__board" style={{ width: doc.width * scale, height: doc.height * scale }} aria-label={t('artboard')} role="group">
      <canvas ref={canvasRef} className="art-stage__canvas" />
      <div ref={overlayRef} className={`art-stage__overlay${cropping ? ' is-cropping' : ''}`} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp} onPointerLeave={() => setHover(null)} onDoubleClick={onDoubleClick} onContextMenu={onContextMenu}>
        <span ref={hoverRef} className="art-stage__hover" />
        <div ref={highlightRef} className="art-stage__highlights">
          {changed.map(layer => <span key={`${highlight.key}-${layer.id}`} className={`art-stage__changed${highlight.persist ? ' is-held' : ''}`} ref={element => placeBox(element, layer, scale)} />)}
        </div>
        {single && <SelectionBox ref={selectionRef} layer={single} scale={scale} readOnly={readOnly || !!cropping} onHandleDown={onHandleDown} />}
        <div ref={guidesRef} className="art-stage__guides" />
        {editingLayer && <TextEditor key={editingLayer.id} layer={editingLayer} scale={scale}
          onCommit={text => { editor.edit(null); if (!text.trim()) editor.remove([editingLayer.id]); else if (text !== editingLayer.text) editor.update(editingLayer.id, { text }, { key: null, label: 'text' }); }} />}
      </div>
    </div>
    </div>}
    </div>
    {pager && <>
      <Button className={`art-stage__pager is-prev${pager.place}`} size="small" variant="rounded" color="current" preventLoading icon={<IconChevronLeft />}
        aria-label={t('previousPage')} disabled={pager.at === 0} onClick={() => editor.stepPage(-1)} />
      <Button className={`art-stage__pager is-next${pager.place}`} size="small" variant="rounded" color="current" preventLoading icon={<IconChevronRight />}
        aria-label={t('nextPage')} disabled={pager.at === pager.count - 1} onClick={() => editor.stepPage(1)} />
    </>}
  </div>;
}
